import { action, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import type { RequireAllProps } from '../../../store/utils/Dto';
import Sellable, {
  type SavingAction,
  type SellableConstructorDto,
  type SellableDto,
} from '../../products/store/sellable/Sellable';

export default class Bundle extends Sellable<BundleDto> {
  static Schema = z.object({
    name: z.string().min(1),
    sku: z.string().min(1),
    price: z.coerce.number().min(0),
    currency: z.string().min(1),
    productsIds: z
      .array(z.string())
      .min(2, 'At least two products are required for the bundle'),
    useBundleKey: z.boolean(),
  });

  productsIds = [];

  useBundleKey = false;

  readonly type = 'bundle';

  constructor(
    dto: BundleConstructorDto = {},
    onSave?: (product: Bundle) => Promise<void>,
  ) {
    super(dto, onSave as (product: Sellable) => Promise<void>);

    makeObservable(this, {
      productsIds: observable,
      useBundleKey: observable,
      changeProductsIds: action.bound,
    });

    Object.assign(this, dto);

    this.validator.validate();
  }

  changeProductsIds(value: string[]) {
    this.handleChange({ name: 'productsIds' as keyof SellableDto, value });
  }

  toJSON(): RequireAllProps<BundleDto> {
    return {
      ...super.toJSON(),
      productsIds: [...this.productsIds],
      useBundleKey: this.useBundleKey,
    };
  }
}

export interface BundleDto extends SellableDto {
  productsIds?: string[];
  useBundleKey?: boolean;
}

export interface BundleConstructorDto
  extends SellableConstructorDto, BundleDto {
  savingAction?: SavingAction;
}
